import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Calendar, User, Factory, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function setHead(title, description) {
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }
  meta.setAttribute("content", description || "");
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [cats, setCats] = useState([]);
  const [active, setActive] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setHead(
      "Building Insights — LumberLoop USA",
      "Product spotlights, building trends, contractor tips and manufacturer news from the LumberLoop USA network."
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c] = await Promise.all([
        axios.get(`${API}/public/insights`, { params: active === "all" ? {} : { category: active } }),
        axios.get(`${API}/public/insight-categories`),
      ]);
      setPosts(p.data);
      setCats(c.data);
    } catch (e) {
      console.error("Failed to load insights:", e);
    } finally {
      setLoading(false);
    }
  }, [active]);

  useEffect(() => { load(); }, [load]);

  const featured = posts[0];
  const rest = posts.slice(1);
  const totalCount = useMemo(() => cats.reduce((s, c) => s + c.count, 0), [cats]);

  return (
    <div data-testid="insights-page">
      {/* HERO */}
      <section className="relative bg-[#1A365D] text-white overflow-hidden border-b-2 border-[#1A365D]">
        <div className="stripes-stars absolute inset-0 opacity-30"></div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20">
          <div className="label-tag text-slate-300">Industry Knowledge</div>
          <h1 className="font-heading text-5xl md:text-7xl font-black uppercase mt-2 leading-none">
            Building Insights<span className="text-[#C62828]">.</span>
          </h1>
          <p className="mt-5 text-lg text-slate-200 max-w-2xl">
            Real-world commentary on lumber, building materials, and the channel — product spotlights, contractor tips, and the market moves manufacturers should know.
          </p>
        </div>
        <div className="h-2 stripes-flag"></div>
      </section>

      {/* CATEGORY FILTER */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10">
        <div className="flex items-center gap-2 flex-wrap" data-testid="insights-category-filter">
          <FilterPill active={active === "all"} onClick={() => setActive("all")} testid="filter-all">
            All <span className="opacity-60 ml-1">({totalCount})</span>
          </FilterPill>
          {cats.map((c) => (
            <FilterPill key={c.name} active={active === c.name} onClick={() => setActive(c.name)} testid={`filter-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
              {c.name} <span className="opacity-60 ml-1">({c.count})</span>
            </FilterPill>
          ))}
        </div>
      </section>

      {/* POSTS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-16">
        {loading ? (
          <div className="py-20 text-center font-mono-ll text-sm tracking-widest text-slate-500">LOADING…</div>
        ) : posts.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {featured && <FeaturedCard post={featured} />}
            {rest.length > 0 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10" data-testid="insights-grid">
                {rest.map((p) => <PostCard key={p.id} post={p} />)}
              </div>
            )}
          </>
        )}
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-[#EAEAE5] border-t-2 border-[#1A365D]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="label-tag">Get matched today</div>
            <h2 className="font-heading text-3xl md:text-5xl font-black uppercase mt-2 leading-none text-[#1A365D]">
              Need product fast? <span className="text-[#C62828]">Skip the search.</span>
            </h2>
            <p className="mt-4 text-slate-700 max-w-2xl">
              Submit your specs and we&apos;ll route you to local dealers in your market within minutes.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/request-quote" className="btn-primary inline-flex items-center gap-2" data-testid="insights-bottom-cta">
              Request a Quote <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function FilterPill({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-xs font-mono-ll tracking-widest uppercase border-2 transition-colors ${
        active
          ? "bg-[#1A365D] text-white border-[#1A365D]"
          : "bg-white text-[#1A365D] border-[#1A365D] hover:bg-[#1A365D] hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function FeaturedCard({ post }) {
  return (
    <Link
      to={`/insights/${post.slug}`}
      className="block group brutal-card overflow-hidden hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform"
      data-testid={`insight-featured-${post.slug}`}
    >
      <div className="grid md:grid-cols-12 gap-0">
        <div className="md:col-span-7 aspect-[16/9] md:aspect-auto bg-slate-200 overflow-hidden">
          {post.featured_image_url ? (
            <img src={post.featured_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"/>
          ) : (
            <Placeholder/>
          )}
        </div>
        <div className="md:col-span-5 p-7 md:p-10 flex flex-col justify-center">
          <span className="self-start px-2 py-1 bg-[#C62828] text-white text-[10px] font-mono-ll tracking-widest uppercase">
            Featured · {post.category}
          </span>
          <h2 className="mt-4 font-heading text-3xl md:text-4xl font-black uppercase leading-tight text-[#1A365D] group-hover:text-[#C62828] transition-colors">
            {post.title}
          </h2>
          <p className="mt-3 text-slate-700 line-clamp-3">{post.excerpt}</p>
          <Meta post={post} className="mt-5"/>
          <span className="mt-5 inline-flex items-center gap-2 font-mono-ll text-xs tracking-widest uppercase text-[#C62828]">
            Read article <ArrowRight size={14}/>
          </span>
        </div>
      </div>
    </Link>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/insights/${post.slug}`}
      className="block group brutal-card-flat overflow-hidden hover:translate-x-[-2px] hover:translate-y-[-2px] transition-transform"
      data-testid={`insight-card-${post.slug}`}
    >
      <div className="aspect-[16/10] bg-slate-200 overflow-hidden">
        {post.featured_image_url ? (
          <img src={post.featured_image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-300"/>
        ) : (
          <Placeholder/>
        )}
      </div>
      <div className="p-5">
        <span className="px-2 py-1 bg-[#1A365D] text-white text-[10px] font-mono-ll tracking-widest uppercase">{post.category}</span>
        <h3 className="mt-3 font-heading text-xl font-bold uppercase leading-tight text-[#1A365D] group-hover:text-[#C62828] transition-colors line-clamp-2">
          {post.title}
        </h3>
        <p className="mt-2 text-sm text-slate-700 line-clamp-2">{post.excerpt}</p>
        <Meta post={post} className="mt-4 text-[10px]"/>
      </div>
    </Link>
  );
}

function Meta({ post, className = "" }) {
  return (
    <div className={`flex items-center gap-3 flex-wrap font-mono-ll tracking-widest uppercase text-slate-500 ${className}`}>
      <span className="flex items-center gap-1"><Calendar size={12}/> {fmtDate(post.published_at || post.created_at)}</span>
      {post.author && <span className="flex items-center gap-1"><User size={12}/> {post.author}</span>}
      {post.manufacturer_name && <span className="flex items-center gap-1"><Factory size={12}/> {post.manufacturer_name}</span>}
    </div>
  );
}

function Placeholder() {
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1A365D] to-[#C62828]">
      <BookOpen className="text-white/80" size={48} strokeWidth={1.5}/>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="brutal-card-flat p-12 text-center">
      <BookOpen className="mx-auto text-slate-400" size={48} strokeWidth={1.5}/>
      <h2 className="mt-4 font-heading text-2xl font-bold uppercase text-[#1A365D]">No posts yet in this category</h2>
      <p className="mt-2 text-slate-600">Check back soon — fresh articles drop every week.</p>
    </div>
  );
}
